import React, { useEffect, useMemo, useState } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import { runInAction } from 'mobx';

import {
	CourseEntity, CourseOfferingEntity, ProgramRuleEntity, RequirementEntity,
} from 'Models/Entities';

import { DataIndex, indexByPropertyMutate } from 'Util/DataIndexUtils';

import { IGeneratedPlanFeedback } from 'Queries/Hooks/StudyPlan/types';

import { BannerItem } from 'Views/Components/Accordion';
import Toggle from 'Views/Components/Toggle/Toggle';
import { Select, OnChangeValue } from 'Views/Components/Select/Select';
import CourseInfoModal from 'Views/Pages/Frontend/StudyPlanWizard/CourseInfoModal';
import { sortTermsAndGroupByStudyPeriod } from 'Util/TermUtils';
import InfoIconButton from 'Views/Components/InfoIconButton/InfoIconButton';
import CompleteCreditChip from 'Views/Components/CourseAccordion/CompleteCreditChip';
import Chip from 'Views/Components/Chip/Chip';
import { Conditional } from 'Views/Components/Conditional/Conditional';
import { capitalize, toLower } from 'lodash';

export interface CourseSelectionItemProps {
	requirement: RequirementEntity | ProgramRuleEntity
	course: CourseEntity;
	compulsory: boolean;
	courseOfferings: CourseOfferingEntity[];
	selectedOfferingId?: string;
	selectedCampusId?: string;
	selected: boolean;
	completed: boolean;
	requisiteWarningsByCourseId: DataIndex<IGeneratedPlanFeedback['requisiteWarnings']>;
	hasDuplicates: boolean;
	updatePlanSelections: (
		locationId: string,
		courseId: string,
		enabled: boolean,
		completed: boolean,
		selectedCourseOfferingId?: string,
	) => void;
	preferredCampusId: string;
}

export const CourseSelectionItem = observer((props: CourseSelectionItemProps) => {
	const {
		requirement,
		course,
		selectedOfferingId,
		selectedCampusId,
		selected,
		completed,
		compulsory,
		courseOfferings,
		requisiteWarningsByCourseId,
		updatePlanSelections,
		hasDuplicates,
	} = props;
	const dropdownSelectionState = useLocalStore<{
		selectedCampusId?: string,
		selectedOfferingId?: string,
	}>(() => ({
		selectedCampusId: selectedCampusId,
		selectedOfferingId: selectedOfferingId,
	}));

	const [showCourseInfoModal, setShowCourseInfoModal] = useState(false);

	const enabledCourseOfferings = useMemo(
		() => courseOfferings.filter(offering => offering.enabled),
		[courseOfferings],
	);

	// Since dropdown selection state does not update if the data is updated outside the component
	// using useEffect to update the selection state whenever the data changes
	useEffect(() => {
		if (selectedOfferingId !== dropdownSelectionState.selectedOfferingId) {
			runInAction(() => {
				dropdownSelectionState.selectedOfferingId = selectedOfferingId;
			});
		}
	}, [dropdownSelectionState, selectedOfferingId]);

	useEffect(() => {
		if (selectedCampusId !== dropdownSelectionState.selectedCampusId) {
			runInAction(() => {
				dropdownSelectionState.selectedCampusId = selectedCampusId;
			});
		}
	}, [dropdownSelectionState, selectedCampusId]);

	// get unique list of campus dropdown options available
	// for this item's set of offerings
	const campusOptions = useMemo(
		() => {
			// index offerings by campus ID to generate a unique
			// map of campuses
			const offeringsByCampusId = indexByPropertyMutate<
				CourseOfferingEntity,
				{
					display: string,
					value: string,
				}
			>(
				enabledCourseOfferings,
				offering => offering.campus.id,
				offering => ({
					display: offering.campus.name,
					value: offering.campus.id,
				}),
			);

			// convert map to list and sort alphabetically,
			// descending
			const uniqueCampusOptions = Object.values(offeringsByCampusId);
			const sortedCampusOptions = uniqueCampusOptions
				.sort((a, b) => a.display.localeCompare(b.display));

			return sortedCampusOptions;
		},
		[enabledCourseOfferings],
	);

	// get all campus offerings for the current selected campus
	const selectedCampusOfferings = enabledCourseOfferings
		.filter(offering => offering.campusId === dropdownSelectionState.selectedCampusId);

	// generate a list of terms to serve as proxy
	// for selecting a Course Offering
	const termOptions = selectedCampusOfferings
		.sort((a, b) => sortTermsAndGroupByStudyPeriod(a.term, b.term))
		.map(offering => ({
			display: offering.term.name,
			value: offering.id,
		}));

	// Toggles entire item on or off
	const toggleCourseSelection = (newDropdownSelectionState: boolean, newCompletionStatus?: boolean) => {
		if (!newDropdownSelectionState) {
			// if turning off, clear state
			runInAction(() => {
				dropdownSelectionState.selectedCampusId = undefined;
				dropdownSelectionState.selectedOfferingId = undefined;
			});
		}

		// update the plan selections
		updatePlanSelections(
			requirement.id,
			course.id,
			newDropdownSelectionState,
			newCompletionStatus ?? completed,
		);
	};

	// When an offering is marked as completed
	// send the subset of state upwards via callback with the
	// new complete/incomplete offerings in this item
	const updateCompletedStatus = (data: OnChangeValue<boolean, false>) => {
		if (!selected || !data) {
			return;
		}

		// If the course is marked as complete, update course selections
		if (data.value) {
			// Clear campus and term selections
			runInAction(() => {
				dropdownSelectionState.selectedCampusId = undefined;
				dropdownSelectionState.selectedOfferingId = undefined;
			});

			updatePlanSelections(
				requirement.id,
				course.id,
				true, // User can only select completion status when the course is selected,
				data.value,
			);
			return;
		}

		// If the course is marked as incomplete, select the default course offering
		toggleCourseSelection(true, data.value);
	};

	/*
		When a campus is selected
		- update locally tracked campus state
		- determine whether we need to update the local mobx state offering id
			- (update it if we do)
			- (update it if we do)
	 */
	const updateSelectedCampus = (data: OnChangeValue<string, false>) => {
		const newCampusId = data?.value;

		if (!newCampusId) {
			return;
		}

		const currentSelectedOffering = enabledCourseOfferings
			.find(offering => offering.id === dropdownSelectionState.selectedOfferingId);

		// Early return if campus is already selected
		if (currentSelectedOffering?.campusId === newCampusId) {
			return;
		}

		// if the campus changes and the current term/offering
		// does not belong to the selected campus, then update
		// the selected term/offering

		// course offerings for the new campus, sorted by term
		const offeringsForNewCampus = enabledCourseOfferings
			.filter(offering => offering.campusId === newCampusId)
			.sort((a, b) => sortTermsAndGroupByStudyPeriod(a.term, b.term));

		const defaultTermOffering = offeringsForNewCampus
			.find(offering => offering.isDefaultTerm);

		const secondaryOfferingSelection = offeringsForNewCampus[0];

		// if no matching offering exists, default to the first option in the list
		const newOfferingId = defaultTermOffering?.id ?? secondaryOfferingSelection?.id;

		if (newOfferingId === undefined) {
			throw new Error('New Offering for selected Campus could not be found');
		}

		// update local and propogate to parent/wizard state
		runInAction(() => {
			dropdownSelectionState.selectedCampusId = newCampusId;
			dropdownSelectionState.selectedOfferingId = newOfferingId;
		});

		// update the new selected offerings and clear the offering completion status
		updatePlanSelections(
			requirement.id,
			course.id,
			true, // User can only select campus when the course is selected
			completed,
			newOfferingId,
		);
	};

	// When an offering is selected
	// send the subset of state upwards via callback with the
	// new selected/unselected offerings in this item
	const updateSelectedCourseOffering = (data: OnChangeValue<string, false>) => {
		const currentOffering = data?.value;
		if (!currentOffering || typeof currentOffering !== 'string') {
			return;
		}

		updatePlanSelections(
			requirement.id,
			course.id,
			true, // User can only select campus when the course is selected
			completed,
			currentOffering,
		);
	};

	const duplicateBanner = hasDuplicates && selected
		? (
			<BannerItem
				key={`${selectedOfferingId}-${course.id}`}
				id={course.id}
				title={capitalize(window.lexicon.duplicate)}
				subtitle={capitalize(window.lexicon.duplicateWarningBannerText)}
				showIcon
			/>
		) : undefined;

	const requisiteDescription = useMemo(() => {
		// If there are no warning present, return the regular requisite description
		const topLevelLogicGroup = course.requisiteLogicGroups
			.find(logicGroup => logicGroup.isTopLevelGroup);

		if (!topLevelLogicGroup || !topLevelLogicGroup?.description) {
			return null;
		}

		return (
			<div>
				{topLevelLogicGroup?.description}
			</div>
		);
	}, [course.requisiteLogicGroups]);

	// Retrieve the current requisite label for a listed course
	const requisiteWarningBanner = useMemo(() => {
		// If warnings are present for the current course, return a list of warnings
		if (!(selectedOfferingId && requisiteWarningsByCourseId[course.id])) {
			return null;
		}

		const courseWarnings = requisiteWarningsByCourseId[course.id];
		return (
			<>
				{courseWarnings
					.map(warning => (
						<BannerItem
							key={`${selectedOfferingId}-${course.id}`}
							id={course.id}
							title={warning.title}
							subtitle={warning.description}
							type="warning"
							showIcon
						/>
					))}
			</>
		);
	}, [course.id, requisiteWarningsByCourseId, selectedOfferingId]);

	return (
		<div className="course-selection">
			<div className="course-toggle">
				<Toggle
					ariaLabel={`selection toggle for ${course.code} ${course.name}`}
					disabled={compulsory}
					title={compulsory ? 'This course is compulsory' : undefined}
					checked={selected}
					onChange={e => toggleCourseSelection(e.target.checked)}
				/>
			</div>
			<div className="course">
				<div className="course-info">
					<div className="course-name">
						<p>{course.code}{' '}<span className="txt-bold">{course.name}</span></p>
					</div>
					<div className="course-units">
						<InfoIconButton
							onClick={() => setShowCourseInfoModal(true)}
							ariaLabel={`show ${course.name}`}
						/>
						<p>{course.units} {toLower(window.lexicon.unitsShort)}</p>
					</div>
					<Conditional
						condition={requisiteWarningBanner == null}
						component={() => (
							<div className="requisite-description">
								{requisiteDescription}
							</div>
						)}
						componentProps={{}}
					/>
				</div>
				<div className="course-warnings">
					{requisiteWarningBanner}
					{duplicateBanner}
				</div>
				{selected ? (
					<div className="course-actions">
						{enabledCourseOfferings.length > 0 ? (
							<>
								<Select
									label={`select ${course.name}`}
									labelVisible={false}
									searchable={false}
									options={campusOptions}
									modelProperty="selectedCampusId"
									model={dropdownSelectionState}
									isDisabled={completed}
									onAfterChange={updateSelectedCampus}
									inputProps={{
										'aria-label': `Select ${window.lexicon.location} for ${course.name}`,
									}}
								/>
								<Select
									label={`select ${course.name} term`}
									labelVisible={false}
									searchable={false}
									options={termOptions}
									modelProperty="selectedOfferingId"
									model={dropdownSelectionState}
									isDisabled={completed}
									onAfterChange={updateSelectedCourseOffering}
									inputProps={{
										'aria-label': `Select ${window.lexicon.term} for ${course.name}`,
									}}
								/>
							</>
						) : (
							<p className="no-offerings-available">Not currently offered.</p>
						)}
						<Select
							className={`course-completion-dropdown ${completed ? 'completed' : 'incomplete'}`}
							label={`Select ${course.name} completion`}
							labelVisible={false}
							searchable={false}
							options={[
								{
									display: <CompleteCreditChip />,
									value: true,
									className: 'completed',
								},
								{
									display: <Chip chipLabel="Incomplete" />,
									value: false,
									className: 'incomplete',
								},
							]}
							modelProperty="completed"
							model={{ completed }}
							onChange={updateCompletedStatus}
						/>
					</div>
				) : null}
			</div>
			{showCourseInfoModal ? (
				<CourseInfoModal
					isOpen={showCourseInfoModal}
					courseId={course.id}
					onCloseModal={() => setShowCourseInfoModal(false)}
				/>
			) : null}
		</div>
	);
});

export default CourseSelectionItem;
